import type { ProjectDetail } from "@esi/shared";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { formatAgo, formatNumber } from "../../i18n/format.ts";
import {
  projectHomePath,
  projectSettingsPath,
  wikiChangesPath,
  wikiNeedsCheckingPath,
  wikiPath,
} from "../../router.tsx";
import { useSession } from "../../session.tsx";
import { useChangesInbox, useWikiIndex } from "../../wiki/queries.ts";
import { can } from "../capabilities.ts";
import {
  summaryUnavailable,
  useDocuments,
  useMembers,
  useProjectGroups,
  useProjectSummary,
} from "../queries.ts";
import { sourcesStatusPath } from "../source-status.ts";
import {
  peopleFigures,
  sourcesFigures,
  sourcesFiguresOf,
  wikiFigures,
  wikiFiguresOf,
} from "./figures.ts";
import { FigureLine, HomeSection, stateOf } from "./HomeSection.tsx";

/**
 * The project summary for a figure section (spec `project-home` "Project summary read"), and
 * whether to derive the figures from the lists instead: only while the API answers 404 for the
 * summary route (an older API during a canary; removed in the next change touching Overview).
 */
export function useFigures(projectId: string) {
  const summary = useProjectSummary(projectId);
  return { summary, fallback: summary.isError && summaryUnavailable(summary.error) };
}

/** Parts of a phone summary line, joined by middots. */
const line = (parts: readonly (string | false | null | undefined)[]) =>
  parts.filter((p): p is string => typeof p === "string" && p !== "").join(" · ");

/**
 * Wiki figures (spec `project-home` "Wiki figures"): the pages the viewer can read, what waits
 * for their review (for viewers who may see drafts), those that need checking (stale or
 * unverified since their last version, when any) and when the latest page they can read was
 * published.
 */
export function WikiFiguresSection({ project }: { project: ProjectDetail }) {
  const { t } = useTranslation();
  const viewDrafts = can(project, "wiki.view_drafts");
  const { summary, fallback } = useFigures(project.id);
  const index = useWikiIndex(project.id, fallback);
  const { queue, inbox } = useChangesInbox(project, fallback && viewDrafts);
  const figures = fallback
    ? index.data
      ? wikiFigures(index.data, viewDrafts ? inbox : null)
      : null
    : summary.data
      ? wikiFiguresOf(summary.data.wiki)
      : null;
  const state = fallback
    ? stateOf(viewDrafts ? [index, queue] : [index], viewDrafts ? 2 : 1)
    : stateOf([summary], viewDrafts ? 2 : 1);
  return (
    <HomeSection
      id="wiki"
      title={t("project.tabs.wiki")}
      link={{ href: wikiPath(project.id), label: t("projectOverview.open") }}
      state={state}
      summaryLine={
        figures
          ? line([
              t("projectOverview.pages", { count: figures.pages }),
              figures.waiting !== null &&
                t("projectOverview.wiki.waitingCount", { count: figures.waiting }),
              figures.needsChecking > 0 &&
                t("projectOverview.wiki.needsCheckingCount", { count: figures.needsChecking }),
            ])
          : undefined
      }
      foot={
        figures?.lastPublishedAt
          ? t("projectOverview.wiki.lastPublished", { when: formatAgo(figures.lastPublishedAt) })
          : figures
            ? t("projectOverview.wiki.neverPublished")
            : undefined
      }
    >
      {figures ? (
        <ul className="divide-y divide-line">
          <FigureLine
            label={t("projectOverview.wiki.pages")}
            value={formatNumber(figures.pages)}
            href={wikiPath(project.id)}
          />
          {figures.waiting !== null ? (
            <FigureLine
              label={t("projectOverview.wiki.waiting")}
              value={formatNumber(figures.waiting)}
              href={wikiChangesPath(project.id)}
            />
          ) : null}
          {figures.needsChecking > 0 ? (
            <FigureLine
              label={t("projectOverview.wiki.needsChecking")}
              value={formatNumber(figures.needsChecking)}
              href={wikiNeedsCheckingPath(project.id)}
              state="warning"
            />
          ) : null}
        </ul>
      ) : null}
    </HomeSection>
  );
}

/**
 * Sources figures (spec `project-home` "Sources figures"): the files the viewer can read, those
 * still processing, those failed (their own uploads, or all for `file.manage_any`), and the
 * restricted count only for `file.restricted_count` (never for a guest).
 */
export function SourcesFiguresSection({ project }: { project: ProjectDetail }) {
  const { t } = useTranslation();
  const { userId } = useSession();
  const { summary, fallback } = useFigures(project.id);
  const documents = useDocuments(project.id, fallback);
  const figures = fallback
    ? documents.data
      ? sourcesFigures(documents.data, project, userId)
      : null
    : summary.data
      ? sourcesFiguresOf(summary.data.sources, project)
      : null;
  const restricted = figures?.restricted ?? 0;
  const manageAny = can(project, "file.manage_any");
  return (
    <HomeSection
      id="sources"
      title={t("project.tabs.files")}
      link={{ href: projectHomePath(project.id, "files"), label: t("projectOverview.open") }}
      state={stateOf(fallback ? [documents] : [summary], 3)}
      summaryLine={
        figures
          ? line([
              t("projectOverview.sources.filesCount", { count: figures.files }),
              figures.processing > 0 &&
                t("projectOverview.sources.processingCount", { count: figures.processing }),
              figures.failed > 0 &&
                t("projectOverview.sources.failedCount", { count: figures.failed }),
            ])
          : undefined
      }
      foot={
        restricted > 0 ? t("projectOverview.sources.restricted", { count: restricted }) : undefined
      }
    >
      {figures ? (
        <ul className="divide-y divide-line">
          <FigureLine
            label={t("projectOverview.sources.files")}
            value={formatNumber(figures.files)}
            href={projectHomePath(project.id, "files")}
          />
          <FigureLine
            label={t("projectOverview.sources.processing")}
            value={formatNumber(figures.processing)}
            href={figures.processing > 0 ? sourcesStatusPath(project.id, "processing") : undefined}
            state={figures.processing > 0 ? "warning" : undefined}
          />
          <FigureLine
            label={
              manageAny
                ? t("projectOverview.sources.failed")
                : t("projectOverview.sources.failedOwn")
            }
            value={formatNumber(figures.failed)}
            href={
              figures.failed > 0 ? sourcesStatusPath(project.id, "failed", !manageAny) : undefined
            }
            state={figures.failed > 0 ? "danger" : undefined}
          />
        </ul>
      ) : null}
    </HomeSection>
  );
}

/**
 * People (spec `project-home` "People figures"), for `project.view_members` only: the owner and
 * the managers by name (from the members list, the one the Members screen reads), the member
 * and group counts (from the summary) and the viewer's role. Guests never get here (the registry
 * omits the section), so neither is requested for them.
 */
export function PeopleSection({ project }: { project: ProjectDetail }) {
  const { t } = useTranslation();
  const { summary, fallback } = useFigures(project.id);
  const members = useMembers(project.id);
  const groups = useProjectGroups(project.id, fallback);
  const unnamed = t("projectOverview.people.unnamed");
  const named = members.data ? peopleFigures(members.data, groups.data, unnamed) : null;
  const counts = fallback
    ? named && groups.data
      ? { members: named.members, groups: named.groups }
      : null
    : (summary.data?.members ?? null);
  const owner =
    project.owner.kind === "entity"
      ? project.ownerName
      : (() => {
          const m = members.data?.find((x) => x.role === "owner");
          return m ? (m.displayName ?? m.email ?? null) : null;
        })();
  return (
    <HomeSection
      id="people"
      title={t("projectOverview.people.title")}
      link={{
        href: projectSettingsPath(project.id, "members"),
        label: t("projectOverview.open"),
      }}
      state={stateOf(fallback ? [members, groups] : [members, summary], 3)}
      summaryLine={
        counts
          ? line([
              t("projectOverview.people.members", { count: counts.members }),
              t("projectOverview.people.groups", { count: counts.groups }),
            ])
          : undefined
      }
      foot={t("projectOverview.people.yourRole", { role: t(`projectRoles.${project.role}.name`) })}
    >
      {named && counts ? (
        <dl className="divide-y divide-line">
          <PeopleLine label={t("projectOverview.people.owner")} value={owner ?? unnamed} />
          {named.managers.length > 0 ? (
            <PeopleLine
              label={t("projectOverview.people.managers", { count: named.managers.length })}
              value={named.managers.join(", ")}
            />
          ) : null}
          <PeopleLine
            label={t("projectOverview.people.in")}
            value={
              <span className="tabular-nums">
                {[
                  t("projectOverview.people.members", { count: counts.members }),
                  t("projectOverview.people.groups", { count: counts.groups }),
                ].join(" · ")}
              </span>
            }
          />
        </dl>
      ) : null}
    </HomeSection>
  );
}

function PeopleLine(props: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 px-4 py-2.5">
      <dt className="shrink-0 text-sm text-fg-secondary">{props.label}</dt>
      <dd className="min-w-0 text-right text-sm font-medium break-words text-fg">{props.value}</dd>
    </div>
  );
}
